import type { Child, Document, Manifest } from '../api/authoring'
import { arrangementsOf, linkIdsIn, storedRegions } from './arrangement'
import { childByLinkId } from './link'
import { declaredSlots, typeOf } from './manifest'
import type { Schema } from './schema'

/** The type whose rank the outline is made of. */
export const HEADING = 'Heading'

/** One heading, in reading order, with where it sits. */
export interface OutlineEntry {
  linkId: string
  text: string
  level: number
  /** How deep in the composed tree it sits, for drawing the map. */
  depth: number
}

/** Something wrong with the outline, as a sentence an author can act on. */
export interface OutlineFault {
  kind: 'multiple-top' | 'skipped-level'
  linkId: string
  detail: string
}

/**
 * A linked child the walk could not look inside.
 *
 * A linked child's content lives on its own stream and this document
 * carries only the placement, so its headings are invisible here. Named
 * rather than ignored: an outline that quietly omitted half a page would
 * be worse than one that says which half it could not see.
 */
export interface Unchecked {
  linkId: string
  type: string
}

export interface Outline {
  entries: OutlineEntry[]
  faults: OutlineFault[]
  unchecked: Unchecked[]
}

/**
 * The document outline of one page, and what is wrong with it.
 *
 * **Read here rather than served, and the reason is where the reading
 * order lives.** An outline is the headings of a *composed* page in the
 * order a visitor meets them, and that order is not the order children
 * were placed — it is each holder's own arrangement field, a Page's
 * `slots` then a Container's `items` then a Grid's `cells`. The platform
 * deliberately never interprets a type's fields, and it has no generic
 * reader for those arrangements; this client does, built on the `x-link`
 * markers the schema already publishes, which is the same machinery the
 * block list is drawn from. Putting the walk in Go would have meant
 * inventing that reader in the core, or teaching the core three type
 * names.
 *
 * **This is a report, and it could only ever have been one.** A skipped
 * level is a fact about a page, and the model validates one document at
 * a time, so no fold can see it; delivery could, but a page must not
 * fail to render because its headings are ranked badly. What is left is
 * something that tells an author, which is what this is.
 *
 * **It reads the authoring document, so it sees the author's own edit.**
 * The alternative was composing from the projection, which lags a
 * command — and a report saying "three top-level headings" about a
 * version the author has already fixed is worse than no report.
 *
 * The cost of living here: nothing else can ask. A publish gate wanting
 * the same answer would need the rule in Go, and would need the
 * arrangement reader with it.
 */
export function outlineOf(document: Document, manifest: Manifest | undefined): Outline {
  const entries: OutlineEntry[] = []
  const unchecked: Unchecked[] = []

  const walk = (
    typeName: string,
    fields: Record<string, unknown>,
    children: Child[] | undefined,
    depth: number,
  ) => {
    // Past a sane depth the page is describing itself in a loop, which
    // the model cannot produce in one document but a future migration
    // might. The walk terminates regardless, on the composer's rule.
    if (depth > 32) return

    for (const linkId of readingOrder(typeName, fields, manifest)) {
      const child = childByLinkId(children, linkId)
      if (!child) continue

      // A linked child carries a target and no fields, so nothing below
      // it is visible from here.
      if (!child.fields) {
        unchecked.push({ linkId: child.linkId, type: child.type })
        continue
      }

      const childFields = child.fields as Record<string, unknown>
      if (child.type === HEADING) {
        const level = typeof childFields.level === 'number' ? childFields.level : 0
        entries.push({
          linkId: child.linkId,
          text: typeof childFields.text === 'string' ? childFields.text : '',
          level,
          depth,
        })
        continue
      }
      walk(child.type, childFields, child.children, depth + 1)
    }
  }

  walk(document.type, (document.fields ?? {}) as Record<string, unknown>, document.children, 0)
  return { entries, faults: faultsIn(entries), unchecked }
}

/**
 * The link ids a node arranges, in the order a visitor meets them.
 *
 * Regions first in the order the type declares them — a Page means
 * header before body before footer, and the declaration is the only
 * thing that says so — then any region the document holds that the type
 * does not declare, which is content a reader can see and the
 * declaration has stopped describing.
 */
function readingOrder(
  typeName: string,
  fields: Record<string, unknown>,
  manifest: Manifest | undefined,
): string[] {
  const declared = typeOf(manifest, typeName)
  const schema = declared?.fields as Schema | undefined
  if (!schema) return []

  const order: string[] = []
  for (const arrangement of arrangementsOf(schema, schema)) {
    if (arrangement.kind !== 'keyed') {
      order.push(...linkIdsIn(arrangement, fields))
      continue
    }
    const stored = storedRegions(arrangement, fields)
    const regions = declaredSlots(manifest, typeName) ?? []
    const extra = stored.filter((region) => !regions.includes(region)).sort()
    for (const region of [...regions, ...extra]) {
      order.push(...linkIdsIn(arrangement, fields, region))
    }
  }
  return order
}

/**
 * What is wrong with a sequence of ranks.
 *
 * Two rules, and deliberately only two — the two that are wrong however
 * a page is designed. **More than one top-level heading** means the page
 * claims to be about two things, and a screen reader's document view
 * shows it as two. **A skipped level** — a 2 followed by a 4 — leaves a
 * gap in the outline that the same view renders as a missing section.
 *
 * A page whose first heading is not rank 1 is *not* reported, and that
 * absence is a decision: a site's own name is often the page's `h1`, in
 * a header the page did not declare and this walk cannot see. Reporting
 * it would fire on every correctly-built page on such a site, and a
 * report that cries wolf is one an author learns to close.
 */
function faultsIn(entries: OutlineEntry[]): OutlineFault[] {
  const faults: OutlineFault[] = []
  const ranked = entries.filter((entry) => entry.level > 0)
  if (ranked.length === 0) return faults

  const top = Math.min(...ranked.map((entry) => entry.level))
  const tops = ranked.filter((entry) => entry.level === top)
  if (tops.length > 1) {
    for (const entry of tops.slice(1)) {
      faults.push({
        kind: 'multiple-top',
        linkId: entry.linkId,
        detail: `A second heading at level ${top}. A page is about one thing, so one heading should be its most important.`,
      })
    }
  }

  let previous = ranked[0].level
  for (const entry of ranked.slice(1)) {
    if (entry.level > previous + 1) {
      faults.push({
        kind: 'skipped-level',
        linkId: entry.linkId,
        detail: `Jumps from level ${previous} to ${entry.level}. Levels should step down one at a time.`,
      })
    }
    previous = entry.level
  }
  return faults
}
